import { CinemaDto } from '@cineamo/api/models/cinema/CinemaDto.types';

import { getCinemaById } from '@cineamo/api/resources/cinema/cinema-api';
import { getMovieTourStops } from '@cineamo/api/resources/movie-tour-stop/movie-tour-stop-api';
import { MovieTourStopDto } from '@cineamo/api/resources/movie-tour-stop/movie-tour-stop.types';

export type TourStop = MovieTourStopDto & {
    cinema?: CinemaDto;
};

/**
 * The landing-page configuration stores the tour id as a string inside its JSON blob. It is
 * resolved to the numeric id the API expects here, at the one boundary it enters the app through.
 */
export function parseMovieTourId(movieTourId: string | null | undefined): MovieTourStopDto['movieTourId'] | null {
    if (!movieTourId) {
        return null;
    }
    const parsed = Number.parseInt(movieTourId, 10);
    return Number.isNaN(parsed) ? null : parsed;
}

/**
 * Every stop of a tour, oldest first.
 *
 * Deliberately not `getAllListItems`: `getMovieTourStops` swallows a failed request into `null`,
 * which that helper flattens into an empty page, so a transient API error is indistinguishable
 * from a tour that has no stops. The browser-side refetch would render that as the whole section
 * vanishing from a page that was showing it a second earlier, so here a failure stays a failure
 * and the caller decides what to do with it.
 *
 * @throws when any page of the list cannot be read.
 */
async function fetchAllTourStops(movieTourId: MovieTourStopDto['movieTourId']): Promise<MovieTourStopDto[]> {
    const readPage = async (page: number) => {
        const response = await getMovieTourStops({
            movieTourIds: [movieTourId],
            sort: 'startDatetime',
            order: 'asc',
            page
        });
        if (!response) {
            throw new Error(`Failed to read page ${page} of the stops of movie tour ${movieTourId}`);
        }
        return response;
    };

    const firstPage = await readPage(1);
    const pageCount = firstPage._page_count ?? 1;
    const nextPages = await Promise.all(
        Array.from({ length: Math.max(pageCount - 1, 0) }, (_, index) => readPage(index + 2))
    );

    return [firstPage, ...nextPages].flatMap((page) => page._embedded?.['movie-tour-stops'] ?? []);
}

/**
 * The stops of a movie tour with their cinema resolved, as the landing page renders them. Shared by
 * the static render of the page and by the refetch `CinemaTourSection` runs in the browser, so the
 * two cannot drift apart.
 *
 * The cinema has to be fetched separately — the list endpoint returns `cinemaId` only and has no
 * `include` — but only once per *cinema*, and not at all for the ones in `knownCinemas`. That is
 * what keeps the browser-side refetch cheap: it passes the cinemas of the stops already on screen,
 * so the common case (nothing changed, or one stop added) costs one request in total.
 *
 * A cinema that fails to load costs the card its logo, nothing more, so unlike the stops themselves
 * it is not worth failing the whole list over.
 */
export async function fetchTourStops(
    movieTourId: MovieTourStopDto['movieTourId'],
    knownCinemas: ReadonlyMap<CinemaDto['id'], CinemaDto> = new Map()
): Promise<TourStop[]> {
    const stops = await fetchAllTourStops(movieTourId);

    const missingCinemaIds = [...new Set(stops.map((stop) => stop.cinemaId).filter((id) => id !== null))].filter(
        (id) => !knownCinemas.has(id)
    );
    const cinemas = new Map([
        ...knownCinemas,
        ...(await Promise.all(missingCinemaIds.map(async (id) => [id, await getCinemaById(id)] as const)))
    ]);

    return stops.map((stop) => (stop.cinemaId ? { ...stop, cinema: cinemas.get(stop.cinemaId) ?? undefined } : stop));
}
